/**
 * grid-layout — the uniform dashboard grid model (uniform-grid PR-2).
 *
 * Pure functions, zero React / zero DOM. This module is the single
 * source of truth for:
 *
 *   * the grid units (12 columns × 40px row units × 16px gap) and the
 *     px math (`cellPx`),
 *   * the DEFAULT_H table — the per-element / per-chart-type default
 *     heights (EXACT MIRROR of the Python table, see below),
 *   * `resolveHeight` — explicit `height` > legacy `row_span` mapping
 *     > per-type default (same precedence as the engine's flatten),
 *   * `placeItems` — the deterministic sparse auto-placement simulator
 *     (mirrors CSS Grid `grid-auto-flow: row` sparse packing),
 *   * `stretchToFill` — the user's vertical stretch rule ("cuando hayan
 *     elementos en una misma fila, ojalá que los elementos más bajos se
 *     expandan para no tener espacios vacíos").
 *
 * MIRROR: packages/report-engine/src/report_engine/config_compat.py —
 * `GRID_ROW_UNIT_PX` / `GRID_GAP_PX` / `DEFAULT_H_CHART` /
 * `DEFAULT_H_BY_CHART_TYPE` / `DEFAULT_H_BY_ELEMENT` / `default_h()` /
 * `legacy_height()`. Keep BOTH sides in sync; the cross-language
 * contract test (`tests/fixtures/height_contract.json`, validated by
 * pytest AND vitest) fails CI when they drift.
 */

// ── Units ────────────────────────────────────────────────────────────

/** Number of grid columns (unchanged from the legacy 12-col layout). */
export const GRID_COLS = 12;

/** Height of one grid row unit in pixels.
 *  MIRROR: config_compat.GRID_ROW_UNIT_PX. */
export const ROW_UNIT_PX = 40;

/** Grid gap in pixels (the historical `gap-4`) — gaps INSIDE a span
 *  count toward the item's pixel height (RGL/Grafana formula).
 *  MIRROR: config_compat.GRID_GAP_PX. */
export const GAP_PX = 16;

/** Hard ceiling for a grid height in row units — the same bound the
 *  builder's publish-time validator enforces
 *  (`builder/service.py::_LAYOUT_FIELD_RANGES`, height 1..40). A value
 *  above it can only reach the renderer by bypassing publish validation
 *  (direct SQL write), so the render layer FAILS LOUD (`resolveHeight`
 *  / `placeItems` throw → per-section error card in `DashboardBody`)
 *  instead of clamping silently (Rule 1: no silent fallbacks). */
export const MAX_GRID_H = 40;

/** Pixel height of an item spanning `h` row units:
 *  `h*40 + (h-1)*16` — h=7 → 376px. */
export function cellPx(h: number): number {
  const units = Math.max(1, Math.round(h));
  return units * ROW_UNIT_PX + (units - 1) * GAP_PX;
}

// ── DEFAULT_H table (mirror of config_compat.py) ─────────────────────

/** Default height (row units) for a generic chart card. Also the
 *  multiplier of the legacy `row_span` mapping (see `resolveHeight`).
 *  MIRROR: config_compat.DEFAULT_H_CHART = 7. */
export const DEFAULT_H_CHART = 7;

/** Per-`chart_type` DEFAULT_H overrides — these absorb the per-type
 *  pixel offsets `GenericChart` used to apply (radar +60, funnel +40,
 *  diverging +20, bullet −40) plus the viewBox-based natural heights
 *  (timeline 200px, calendar_heatmap 140px) and the mini-table chart
 *  (max-h-280). `map` is resolved separately from `map_variant`
 *  (chile 11 / south_america 9) in `defaultH`.
 *  MIRROR: config_compat.DEFAULT_H_BY_CHART_TYPE. */
export const DEFAULT_H_BY_CHART_TYPE: Readonly<Record<string, number>> = {
  // Epic v3 scorecards. `metric_rows` is a short stack of meters; the
  // card grid holds a header + N meter rows + a footer note per card,
  // so it needs the same room as a table.
  metric_rows: 5,
  metric_strip: 4,
  ranked_list: 8,
  ai_readout: 6,
  metric_card_grid: 9,
  // A single row of period KPI tiles (compare + select). Short.
  kpi_period_tiles: 3,
  stacked_share_combo: 7,
  // Pricing-Brand migration (2026-09-08): a headline + a 34px scale,
  // and a thin bar plus one legend row per declared segment.
  index_meter: 5,
  share_breakdown: 7,
  // The saved-views portfolio (DSA): a summary strip over two rows of
  // view cards. MIRROR: config_compat.DEFAULT_H_BY_CHART_TYPE.
  saved_views_portfolio: 10,
  // Chrome-less tinted surfaces: a banner and a key/value strip.
  callout: 3,
  meta_grid: 3,
  radar: 8,
  funnel: 8,
  diverging_stacked_bar: 8,
  bullet: 6,
  timeline: 5,
  calendar_heatmap: 4,
  table: 7,
};

/** Per-`element` DEFAULT_H for non-chart items.
 *  MIRROR: config_compat.DEFAULT_H_BY_ELEMENT. */
export const DEFAULT_H_BY_ELEMENT: Readonly<Record<string, number>> = {
  table: 9, // ≈ header + 10 rows + pagination footer
  kpi: 2, // 96px ≥ the KPI card's min-h 76px (strip ignores it today)
  text: 1, // single text line
};

/** Dashboard element kinds the grid places. */
export type GridElement = "chart" | "table" | "kpi" | "text";

/** Default grid height (row units) for an element + chart metadata.
 *  MIRROR: config_compat.default_h(element, props). */
/**
 * How far BELOW its natural height a widget may be dragged, as a
 * fraction of that height.
 *
 * The natural height is not a guess — ``DEFAULT_H_BY_CHART_TYPE`` is
 * the number the platform already publishes for each chart type, the
 * one the dashboard lays out with. A resize floor derived from it says
 * "you may make this smaller, but not so small that the type stops
 * working", without inventing a second table of numbers that could
 * drift from the first.
 *
 * 0.7 rather than 1.0 on purpose: a hard floor at the natural height
 * would mean charts could only ever grow, which is not what a personal
 * board is for. 30% of slack is enough to tighten a layout and not
 * enough to reach the sizes where a dense chart (a card grid of four
 * countries, a ranked list of twenty rows) turns into a scrollbar.
 */
export const MIN_H_RATIO = 0.7;

/** The smallest height a widget of this kind may be dragged to, in grid
 *  rows. Derived from the type's natural height; never below 2 rows,
 *  which is the smallest cell that can hold a title plus one line. */
export function minH(
  element: GridElement,
  chartType?: string | null,
  mapVariant?: string | null,
): number {
  const natural = defaultH(element, chartType, mapVariant);
  return Math.max(2, Math.ceil(natural * MIN_H_RATIO));
}

export function defaultH(
  element: GridElement,
  chartType?: string | null,
  mapVariant?: string | null,
): number {
  if (element === "chart") {
    const t = chartType || "bar"; // engine lookup default — same as Python
    if (t === "map") {
      return (mapVariant || "south_america") === "chile" ? 11 : 9;
    }
    return DEFAULT_H_BY_CHART_TYPE[t] ?? DEFAULT_H_CHART;
  }
  return DEFAULT_H_BY_ELEMENT[element] ?? DEFAULT_H_CHART;
}

/** The fields `resolveHeight` reads off a flattened config item. */
export interface HeightSource {
  element: GridElement;
  /** Chart `type` from the serialized config (`props.type` engine-side —
   *  the flattened item carries it as `type`). */
  chartType?: string | null;
  /** `map_variant` for `chartType === "map"`. */
  mapVariant?: string | null;
  /** Explicit `height` (row units) when the config declares one. */
  height?: number | null;
  /** Legacy `row_span` (1..3) — deprecated, mapped below. */
  rowSpan?: number | null;
}

/**
 * Resolve an item's grid height in row units.
 *
 * Precedence — EXACT mirror of the engine's flatten resolution
 * (`config_compat.flatten_for_engine` + `legacy_height`):
 *
 *   1. explicit `height` (> 0) wins;
 *   2. legacy `row_span > 1` → `DEFAULT_H_CHART × row_span` (the old
 *      semantics were "span N rows of my NEIGHBORS", and the one real
 *      usage — gating_overall — has generic-chart neighbors, so the
 *      multiplier is the GENERIC chart default, not the element's own);
 *   3. the per-type default (`defaultH`).
 *
 * The portal always receives `height` from the engine (post PR-1), so
 * branch 1 is the hot path there; the builder Preview's TS flattener
 * stamps the RAW authored `height` (no default) and this resolver
 * covers legacy configs with the SAME table — that's the whole point
 * of the mirror.
 *
 * Invalid-input contract (pinned in `height_contract.json`):
 *   * a PRESENT but invalid `height` (wrong type, non-finite, ≤ 0) is
 *     DISCARDED with a `console.warn` (signal for the config author —
 *     documented, never silent) and resolution falls through to the
 *     row_span / default branches. Python's flatten stamps the raw
 *     value and publish-time validation rejects it, so this branch is
 *     only reachable for configs that bypassed validation;
 *   * a present height ABOVE `MAX_GRID_H` throws (fail loud — see
 *     `MAX_GRID_H`); a cell 40+ units tall is config corruption, not a
 *     styling choice.
 *
 * `row_span` uses `Math.trunc` — the EXACT mirror of Python's `int()`
 * in `config_compat.legacy_height` (`int(2.5)` → 2, never 3).
 */
export function resolveHeight(item: HeightSource): number {
  const h = item.height;
  if (h != null) {
    if (typeof h === "number" && Number.isFinite(h) && h > 0) {
      const units = Math.round(h);
      if (units > MAX_GRID_H) {
        throw new Error(
          `Invalid grid height: got ${h} — the maximum is ${MAX_GRID_H} row units ` +
            "(publish-time validation bounds height to 1..40; fix the config)",
        );
      }
      return units;
    }
    // Present-but-invalid → documented discard, never silent.
    console.warn(
      `resolveHeight: discarding invalid height ${JSON.stringify(h)} ` +
        "(expected a finite number > 0) — falling back to the row_span mapping / per-type default. " +
        "Publish-time validation rejects this value; fix the config.",
    );
  }
  const span = Math.trunc(item.rowSpan ?? 1);
  if (span > 1) return DEFAULT_H_CHART * span;
  return defaultH(item.element, item.chartType, item.mapVariant);
}

// ── Placement simulator ──────────────────────────────────────────────

export interface PlacementInput {
  /** Stable identity — carried through to the output placement. */
  key: string;
  /** Column span 1..12 (defensively clamped). */
  width: number;
  /** Row span in grid units (≥ 1, defensively clamped). */
  height: number;
}

/** Explicit grid coordinates, 1-based, ready for inline CSS:
 *  `gridColumn: "${colStart} / span ${colSpan}"`,
 *  `gridRow: "${rowStart} / span ${rowSpan}"`. */
export interface Placement {
  key: string;
  colStart: number;
  rowStart: number;
  colSpan: number;
  rowSpan: number;
}

/** Occupancy matrix — rows are 1-based (index 0 unused is avoided by
 *  offsetting), cols 1..12. Grown on demand. */
type Occupancy = boolean[][];

function markOccupied(occ: Occupancy, p: Placement): void {
  for (let r = p.rowStart; r < p.rowStart + p.rowSpan; r++) {
    while (occ.length <= r) occ.push(new Array<boolean>(GRID_COLS + 1).fill(false));
    const row = occ[r]!;
    for (let c = p.colStart; c < p.colStart + p.colSpan; c++) row[c] = true;
  }
}

function isOccupied(occ: Occupancy, row: number, col: number): boolean {
  return row < occ.length ? (occ[row]?.[col] ?? false) : false;
}

function areaFree(occ: Occupancy, row: number, col: number, w: number, h: number): boolean {
  for (let r = row; r < row + h; r++) {
    if (r >= occ.length) continue; // rows past the grown grid are free
    const rowArr = occ[r]!;
    for (let c = col; c < col + w; c++) {
      if (rowArr[c]) return false;
    }
  }
  return true;
}

/**
 * Deterministic sparse auto-placement — a faithful simulation of CSS
 * Grid's `grid-auto-flow: row` (sparse) packing over a 12-column grid:
 *
 *   * items flow IN AUTHORING ORDER;
 *   * a forward-only cursor tracks the last placement — the next item
 *     scans candidate top-left corners in row-major order STARTING at
 *     the cursor (never before it), row by row, column by column;
 *   * within rows AFTER the cursor's row the scan restarts at column 1
 *     (this is what lets a later item slot under an earlier column —
 *     the sparse "backfill below, never before" semantics);
 *   * the first free `width × height` rectangle wins; the cursor moves
 *     to (that row, colStart + width).
 *
 * Why a JS simulator instead of native auto-placement: (a) the stretch
 * rule needs to know the placement; (b) fully deterministic + testable
 * in vitest without a browser.
 *
 * Input contract (fail loud, Rule 1):
 *   * a NON-FINITE width/height (e.g. `"6px"` cast through the config
 *     types → NaN) THROWS a descriptive Error instead of looping
 *     forever in the scan (`c <= NaN` is always false → the row cursor
 *     would climb unboundedly). `DashboardBody` catches per section
 *     and renders a visible error card — noisy, no freeze, no clamp;
 *   * a height above `MAX_GRID_H` throws (same treatment — values that
 *     bypassed the publish-time 1..40 bound);
 *   * a DUPLICATE key throws — placements are keyed by identity and a
 *     collision silently overlaps two items (`byKey` lookups alias).
 *
 * Within those bounds, width is defensively clamped to 1..12 and
 * height to ≥ 1 (render defense only — `validate_config` rejects
 * out-of-range values at publish time; this never rewrites config
 * data).
 */
export function placeItems(items: readonly PlacementInput[]): Placement[] {
  const occ: Occupancy = [];
  const out: Placement[] = [];
  const seenKeys = new Set<string>();
  let cursorRow = 1;
  let cursorCol = 1;

  for (const item of items) {
    if (seenKeys.has(item.key)) {
      throw new Error(
        `placeItems: duplicate item key "${item.key}" — two items share the same ` +
          "identity (duplicate item ids in the config?); they would overlap in the grid",
      );
    }
    seenKeys.add(item.key);
    if (!Number.isFinite(item.width)) {
      throw new Error(
        `Invalid grid width for item "${item.key}": got ${JSON.stringify(item.width)} ` +
          "(expected a finite number 1..12)",
      );
    }
    if (!Number.isFinite(item.height)) {
      throw new Error(
        `Invalid grid height for item "${item.key}": got ${JSON.stringify(item.height)} ` +
          "(expected a finite number of row units)",
      );
    }
    if (item.height > MAX_GRID_H) {
      throw new Error(
        `Invalid grid height for item "${item.key}": got ${item.height} — the maximum ` +
          `is ${MAX_GRID_H} row units (publish-time validation bounds height to 1..40)`,
      );
    }
    const w = Math.max(1, Math.min(GRID_COLS, Math.round(item.width)));
    const h = Math.max(1, Math.round(item.height));

    let placed: Placement | null = null;
    for (let r = cursorRow; placed === null; r++) {
      const startCol = r === cursorRow ? cursorCol : 1;
      for (let c = startCol; c <= GRID_COLS - w + 1; c++) {
        if (areaFree(occ, r, c, w, h)) {
          placed = { key: item.key, colStart: c, rowStart: r, colSpan: w, rowSpan: h };
          break;
        }
      }
      // Loop terminates: rows past every placed item are fully free and
      // any w ≤ 12 fits at column 1 of an empty row.
    }
    markOccupied(occ, placed);
    out.push(placed);
    cursorRow = placed.rowStart;
    cursorCol = placed.colStart + placed.colSpan;
  }
  return out;
}

/**
 * The user's stretch rule (§5 of the plan, verbatim requirement:
 * "cuando hayan elementos en una misma fila, ojalá que los elementos
 * más bajos se expandan para no tener espacios vacíos"):
 *
 * For each item I: let `cap` be the max row-end among the items that
 * OVERLAP IN ROWS with I (its "visual row"). If the cells directly
 * below I (same columns) are free all the way down to some uniform
 * depth (bounded by `cap`), extend I's rowSpan to cover them. Iterate
 * to a fixed point (spans only grow, bounded by `cap`, so it
 * converges).
 *
 * Deliberate limits:
 *   * VERTICAL only — width gaps are an authoring decision and are
 *     never filled;
 *   * a NON-RECTANGULAR gap (columns with different free depths under
 *     I) is left alone — stretching into it would overlap or produce a
 *     non-rectangular item.
 *
 * The stretched item's component receives the FINAL span's px — the
 * stretch is taller content, never whitespace.
 */
export function stretchToFill(placements: readonly Placement[]): Placement[] {
  const out = placements.map((p) => ({ ...p }));
  // Fixed-point iteration — hard bound as a defensive guard; each pass
  // either grows at least one span or stops.
  for (let pass = 0; pass < out.length + 1; pass++) {
    let changed = false;
    const occ: Occupancy = [];
    for (const p of out) markOccupied(occ, p);

    for (const p of out) {
      const rowEnd = p.rowStart + p.rowSpan;
      // Visual-row cap: max row-end among items overlapping in rows.
      let cap = rowEnd;
      for (const q of out) {
        if (q === p) continue;
        const overlaps = q.rowStart < rowEnd && q.rowStart + q.rowSpan > p.rowStart;
        if (overlaps) cap = Math.max(cap, q.rowStart + q.rowSpan);
      }
      if (cap <= rowEnd) continue;

      // Free depth below p per column, stopping at the first occupied
      // cell or at the cap.
      let uniform = true;
      let depth = -1;
      for (let c = p.colStart; c < p.colStart + p.colSpan; c++) {
        let d = 0;
        for (let r = rowEnd; r < cap; r++) {
          if (isOccupied(occ, r, c)) break;
          d++;
        }
        if (depth === -1) depth = d;
        else if (d !== depth) {
          uniform = false;
          break;
        }
      }
      if (uniform && depth > 0) {
        p.rowSpan += depth;
        markOccupied(occ, p);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return out;
}

// NOTE: `cleanCutRow` (the row-drill band's grid-split line) was removed
// with the in-place-swap redesign — the drill renders INSIDE the table's
// cell (`RowDrillPanel`) and the grid never splits, so the function had
// no consumers left.
